import React from 'react';
import {
  Avatar,
  Button,
  Empty,
  message,
  Popover,
  Segmented,
  Skeleton,
  Tooltip,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faPlus,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { getTasks, PlannerPage, Task, updateTask } from '../../../services/tasks';
import './PlannerViews.css';
import { taskReference } from '../../../utils/publicReferences';

interface CalendarViewProps {
  plannerPage: PlannerPage;
  filters?: Omit<NonNullable<Parameters<typeof getTasks>[0]>, 'plannerPageId'>;
  onTaskOpen?: (taskId: string) => void;
  onCreateTask?: (dueDate: string) => void;
}

type CalendarMode = 'month' | 'agenda';

const PRIORITY_COLORS: Record<Task['priority'], string> = {
  LOW: '#54D654',
  MEDIUM: '#FFE555',
  HIGH: '#FFB255',
  URGENT: '#F05A78',
};

const PRIORITY_ORDER: Record<Task['priority'], number> = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  URGENT: 4,
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function useNarrowCalendar(): boolean {
  const [narrow, setNarrow] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 760 : false
  );

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 759px)');
    const update = () => setNarrow(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return narrow;
}

const taskDateKey = (task: Task) => task.dueDate ? dayjs(task.dueDate).format('YYYY-MM-DD') : '';

const TaskEvent: React.FC<{
  task: Task;
  onOpen?: (taskId: string) => void;
  draggable?: boolean;
  className?: string;
}> = ({ task, onOpen, draggable = true, className = '' }) => (
  <Tooltip title={`${task.priority.toLowerCase()} priority · ${task.status}`} placement="top">
    <button
      type="button"
      className={`planner-calendar-event ${className}`.trim()}
      style={{ borderLeftColor: PRIORITY_COLORS[task.priority] }}
      onClick={(event) => {
        event.stopPropagation();
        onOpen?.(taskReference(task));
      }}
      draggable={draggable}
      onDragStart={(event) => {
        event.stopPropagation();
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/planner-task-id', task.id);
      }}
    >
      <span className="planner-calendar-event-title">{task.title}</span>
      {task.assignee && (
        <Avatar
          size={18}
          src={task.assignee.photoUrl}
          icon={<FontAwesomeIcon icon={faUser} />}
        />
      )}
    </button>
  </Tooltip>
);

const CalendarView: React.FC<CalendarViewProps> = ({
  plannerPage,
  filters = {},
  onTaskOpen,
  onCreateTask,
}) => {
  const queryClient = useQueryClient();
  const isNarrow = useNarrowCalendar();
  const [currentMonth, setCurrentMonth] = React.useState(() => dayjs().startOf('month'));
  const [mode, setMode] = React.useState<CalendarMode>(() =>
    typeof window !== 'undefined' && window.innerWidth < 760 ? 'agenda' : 'month'
  );
  const [dropTarget, setDropTarget] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isNarrow) setMode('agenda');
  }, [isNarrow]);

  const {
    data: tasks = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['tasks', plannerPage.id, filters],
    queryFn: () => getTasks({ plannerPageId: plannerPage.id, ...filters }),
  });

  const rescheduleMutation = useMutation({
    mutationFn: ({ taskId, date }: { taskId: string; date: Dayjs }) =>
      updateTask({
        id: taskId,
        // Local noon keeps this date stable across the majority of timezone conversions.
        dueDate: date.hour(12).minute(0).second(0).millisecond(0).toISOString(),
      }),
    onSuccess: (_, variables) => {
      message.success(`Due date moved to ${variables.date.format('MMM D')}`);
      queryClient.invalidateQueries({ queryKey: ['tasks', plannerPage.id] });
    },
    onError: () => message.error('Could not update the due date'),
    onSettled: () => setDropTarget(null),
  });

  const tasksByDate = React.useMemo(() => {
    const grouped = new Map<string, Task[]>();
    tasks.forEach((task) => {
      const key = taskDateKey(task);
      if (!key) return;
      grouped.set(key, [...(grouped.get(key) || []), task]);
    });
    grouped.forEach((dateTasks) => {
      dateTasks.sort((a, b) => PRIORITY_ORDER[b.priority] - PRIORITY_ORDER[a.priority]);
    });
    return grouped;
  }, [tasks]);

  const unscheduledTasks = React.useMemo(() => tasks.filter((task) => !task.dueDate), [tasks]);

  const visibleDays = React.useMemo(() => {
    const firstDay = currentMonth.startOf('month');
    const daysBeforeMonday = (firstDay.day() + 6) % 7;
    const gridStart = firstDay.subtract(daysBeforeMonday, 'day');
    return Array.from({ length: 42 }, (_, index) => gridStart.add(index, 'day'));
  }, [currentMonth]);

  const agendaGroups = React.useMemo(() => {
    const monthTasks = tasks
      .filter((task) => task.dueDate && dayjs(task.dueDate).isSame(currentMonth, 'month'))
      .sort((a, b) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf());

    const grouped = new Map<string, Task[]>();
    monthTasks.forEach((task) => {
      const key = taskDateKey(task);
      grouped.set(key, [...(grouped.get(key) || []), task]);
    });
    return Array.from(grouped.entries());
  }, [currentMonth, tasks]);

  const openCreateTaskForDate = (date: Dayjs) => onCreateTask?.(date.format('YYYY-MM-DD'));

  const handleDrop = (event: React.DragEvent, date: Dayjs) => {
    event.preventDefault();
    const taskId = event.dataTransfer.getData('text/planner-task-id');
    if (!taskId) return;
    const task = tasks.find((candidate) => candidate.id === taskId);
    if (task?.dueDate && dayjs(task.dueDate).isSame(date, 'day')) {
      setDropTarget(null);
      return;
    }
    rescheduleMutation.mutate({ taskId, date });
  };

  const renderOverflow = (date: Dayjs, dateTasks: Task[]) => (
    <Popover
      trigger="click"
      placement="bottom"
      content={
        <div className="planner-calendar-popover">
          <div className="planner-calendar-popover-title">
            {date.format('dddd, MMMM D')}
          </div>
          <div className="planner-calendar-events">
            {dateTasks.map((task) => (
              <TaskEvent key={task.id} task={task} onOpen={onTaskOpen} draggable={false} />
            ))}
          </div>
        </div>
      }
    >
      <button type="button" className="planner-calendar-more" onClick={(event) => event.stopPropagation()}>
        +{dateTasks.length - 3} more
      </button>
    </Popover>
  );

  const renderMonth = () => (
    <div className="planner-calendar-month-scroll">
      <div className="planner-calendar-weekdays">
        {WEEKDAYS.map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className="planner-calendar-grid">
        {visibleDays.map((date) => {
          const key = date.format('YYYY-MM-DD');
          const dateTasks = tasksByDate.get(key) || [];
          const outside = !date.isSame(currentMonth, 'month');
          const today = date.isSame(dayjs(), 'day');
          return (
            <div
              key={key}
              className={[
                'planner-calendar-day',
                outside ? 'planner-calendar-day--outside' : '',
                today ? 'planner-calendar-day--today' : '',
                dropTarget === key ? 'planner-calendar-day--drop-target' : '',
              ].filter(Boolean).join(' ')}
              onDragEnter={(event) => {
                event.preventDefault();
                setDropTarget(key);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(event) => handleDrop(event, date)}
            >
              <div className="planner-calendar-day-heading">
                <span className="planner-calendar-date-number">{date.date()}</span>
                <Tooltip title={`Create task due ${date.format('MMM D')}`}>
                  <Button
                    type="text"
                    size="small"
                    className="planner-calendar-add-day"
                    aria-label={`Create task due ${date.format('MMMM D')}`}
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => openCreateTaskForDate(date)}
                  />
                </Tooltip>
              </div>
              <div className="planner-calendar-events">
                {dateTasks.slice(0, 3).map((task) => (
                  <TaskEvent key={task.id} task={task} onOpen={onTaskOpen} />
                ))}
                {dateTasks.length > 3 && renderOverflow(date, dateTasks)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderAgenda = () => (
    <div className="planner-agenda">
      {agendaGroups.length === 0 ? (
        <div className="planner-view-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span>No scheduled tasks in {currentMonth.format('MMMM')}.</span>}
          />
          <Button icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => openCreateTaskForDate(currentMonth)}>
            Create a task
          </Button>
        </div>
      ) : agendaGroups.map(([dateKey, dateTasks]) => {
        const date = dayjs(dateKey);
        return (
          <div className="planner-agenda-day" key={dateKey}>
            <div className="planner-agenda-date">
              <strong>{date.format('ddd D')}</strong>
              {date.format('MMMM')}
            </div>
            <div className="planner-agenda-tasks">
              {dateTasks.map((task) => (
                <TaskEvent key={task.id} task={task} onOpen={onTaskOpen} />
              ))}
              <Button
                type="text"
                size="small"
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => openCreateTaskForDate(date)}
              >
                Add task
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (isLoading) {
    return (
      <div className="planner-calendar-shell planner-view-loading" aria-label="Loading calendar">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="planner-view-state">
        <div className="planner-view-state-title">Calendar could not be loaded</div>
        <div className="planner-view-state-copy">Check your connection and try again.</div>
        <Button onClick={() => refetch()}>Try again</Button>
      </div>
    );
  }

  return (
    <section className="planner-calendar-shell" aria-label="Task calendar">
      <div className="planner-view-toolbar planner-calendar-toolbar">
        <div className="planner-calendar-navigation">
          <Tooltip title="Previous month">
            <Button
              aria-label="Previous month"
              icon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={() => setCurrentMonth((month) => month.subtract(1, 'month'))}
            />
          </Tooltip>
          <Button onClick={() => setCurrentMonth(dayjs().startOf('month'))}>Today</Button>
          <Tooltip title="Next month">
            <Button
              aria-label="Next month"
              icon={<FontAwesomeIcon icon={faChevronRight} />}
              onClick={() => setCurrentMonth((month) => month.add(1, 'month'))}
            />
          </Tooltip>
        </div>

        <div className="planner-calendar-title">{currentMonth.format('MMMM YYYY')}</div>

        <div className="planner-calendar-actions">
          <span className="planner-view-summary">
            <span className="planner-view-count">{tasks.length}</span>
            <span>{tasks.length === 1 ? 'task' : 'tasks'}</span>
          </span>
          <Segmented<CalendarMode>
            className="planner-calendar-mode"
            aria-label="Calendar layout"
            value={mode}
            onChange={setMode}
            options={[
              { label: 'Month', value: 'month' },
              { label: 'Agenda', value: 'agenda' },
            ]}
          />
        </div>
      </div>

      {mode === 'month' ? renderMonth() : renderAgenda()}

      {unscheduledTasks.length > 0 && (
        <div className="planner-calendar-unscheduled">
          <div className="planner-calendar-unscheduled-heading">
            <span>Unscheduled</span>
            <span>{unscheduledTasks.length} without a due date · drag onto the calendar</span>
          </div>
          <div className="planner-calendar-unscheduled-list">
            {unscheduledTasks.map((task) => (
              <TaskEvent
                key={task.id}
                task={task}
                onOpen={onTaskOpen}
                className="planner-calendar-unscheduled-task"
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default CalendarView;
